import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';
import { api } from '@/lib/api.ts';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { PasswordInput } from '@/components/password-input';

export const Route = createFileRoute('/recovery/$token')({
  component: PasswordRecoveryPage,
  staticData: { label: msg`Set a new password` },
});

function PasswordRecoveryPage() {
  const { token } = Route.useParams();
  const { t } = useLingui();
  const navigate = useNavigate();
  const confirm = useMutation({
    mutationFn: (newPassword: string) =>
      api.post(`/public/1/auth/passwordrecovery/confirm/${token}/`, {
        new_password: newPassword,
      }),
  });

  const passwordRecoverySchema = z
    .object({
      password: z.string().min(1, t`Password is required`),
      re_password: z.string().min(1, t`Password is required`),
    })
    .refine((values) => values.password === values.re_password, {
      message: t`Passwords do not match`,
      path: ['re_password'],
    });

  const form = useForm({
    defaultValues: { password: '', re_password: '' },
    validators: {
      onChange: passwordRecoverySchema,
      onSubmit: passwordRecoverySchema,
    },
    onSubmit: async ({ value }) => {
      try {
        await confirm.mutateAsync(value.password);
        await navigate({ to: '/login' });
      } catch {
        // Failure is surfaced to the user via `confirm.isError` (the inline alert below).
      }
    },
  });

  return (
    <div className='bg-muted/30 flex min-h-screen items-center justify-center p-4'>
      <div className='bg-background w-full max-w-md space-y-6 rounded-xl border p-8 shadow-sm'>
        <div className='space-y-1 text-center'>
          <h1 className='text-2xl font-semibold tracking-tight'>
            <Trans>Set a new password</Trans>
          </h1>
          <p className='text-muted-foreground text-sm'>
            <Trans>Choose a new password for your account.</Trans>
          </p>
        </div>

        <form
          className='space-y-6'
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <FieldGroup>
            <form.Field name='password'>
              {(field) => (
                <Field data-invalid={field.state.meta.errors.length > 0}>
                  <FieldLabel htmlFor={field.name}>
                    <Trans>New password</Trans>
                  </FieldLabel>
                  <PasswordInput
                    id={field.name}
                    name={field.name}
                    autoComplete='new-password'
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>

            <form.Field name='re_password'>
              {(field) => (
                <Field data-invalid={field.state.meta.errors.length > 0}>
                  <FieldLabel htmlFor={field.name}>
                    <Trans>Confirm password</Trans>
                  </FieldLabel>
                  <PasswordInput
                    id={field.name}
                    name={field.name}
                    autoComplete='new-password'
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>
          </FieldGroup>

          {confirm.isError ?
            <p role='alert' className='text-destructive text-sm'>
              <Trans>Could not update your password. The recovery link may have expired.</Trans>
            </p>
          : null}

          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(submitting) => (
              <Button type='submit' size='lg' className='w-full' disabled={submitting}>
                {submitting ? t`Updating…` : t`Update password`}
              </Button>
            )}
          </form.Subscribe>
        </form>
      </div>
    </div>
  );
}
